import { useState } from 'react';

import {
  Alert,
  Box,
  Button as MuiButton,
  Card,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import ClaimActionRow from '@/james-playground/aa-family-claim/ClaimActionRow';
import ClaimScreen, { ClaimBar } from '@/james-playground/aa-family-claim/ClaimScreen';
import {
  ASK,
  ASKS,
  DOCUMENTS,
  say,
  type Ask,
} from '@/james-playground/aa-family-claim/claim/asks';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { type ClaimState, voiceOf } from '@/james-playground/aa-family-claim/claim/claim';
import { HUB } from '@/james-playground/aa-family-claim/claim/claim.content';
import {
  areaSummary,
  hubAreas,
  isReady,
  nextSteps,
  openIn,
  parseDate,
  progress,
  statusOf,
  qualifyingDate,
  sendBy,
  type AreaSummary,
  type Step,
} from '@/james-playground/aa-family-claim/claim/engine';
import { text } from '@/james-playground/aa-family-claim/claim/facts';
import { SCENARIO } from '@/james-playground/aa-family-claim/claim/scenarios';

const ukDate = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });

const STEP_ICON: Record<Step['kind'], string> = {
  opening: 'microphone',
  voice: 'microphone',
  document: 'camera',
  batch: 'list-check',
  ask: 'pen',
};

/** What a step is called on the hub, and the line under it. */
export const stepCopy = (step: Step, claim: ClaimState) => {
  const v = voiceOf(claim);
  switch (step.kind) {
    case 'opening':
      return { title: HUB.step.opening, detail: HUB.stepDetail.opening };
    case 'voice':
      return { title: say(step.prompt.prompt, v), detail: HUB.step.voice };
    case 'document': {
      const doc = DOCUMENTS.find((d) => d.kind === step.doc);
      return { title: say(doc?.label ?? HUB.step.document, v), detail: say(doc?.prompt ?? '', v) };
    }
    case 'batch':
      return { title: HUB.step.batch, detail: HUB.stepDetail.batch(step.covers.length) };
    case 'ask': {
      const area = areaSummary(step.ask.area, claim.facts).area;
      return { title: say(step.ask.title, v), detail: say(area.label, v) };
    }
  }
};

const StepCard = ({
  step,
  claim,
  onOpen,
}: {
  step: Step;
  claim: ClaimState;
  onOpen: (step: Step) => void;
}) => {
  const { title, detail } = stepCopy(step, claim);
  return (
    <Card
      component='button'
      type='button'
      onClick={() => onOpen(step)}
      sx={{
        p: 2,
        textAlign: 'left',
        cursor: 'pointer',
        border: 1,
        borderColor: 'primary.main',
        bgcolor: 'custom.limeGreen.100',
        font: 'inherit',
        color: 'inherit',
        width: '100%',
        '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'primary.main',
            color: 'common.white',
          }}
        >
          <Icon icon={STEP_ICON[step.kind] as never} fontSize={16} />
        </Box>
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
          <Typography variant='h6'>{title}</Typography>
          <Typography sx={{ color: 'text.primary' }} variant='body2'>
            {detail}
          </Typography>
        </Stack>
        <Icon icon={'chevron-right' as never} fontSize={14} />
      </Stack>
    </Card>
  );
};

const AreaRow = ({
  summary,
  claim,
  last,
  onOpen,
}: {
  summary: AreaSummary;
  claim: ClaimState;
  last: boolean;
  onOpen: () => void;
}) => {
  const v = voiceOf(claim);
  const done = summary.state === 'done';
  const status =
    summary.state === 'in_progress'
      ? HUB.toGo(summary.toGo)
      : HUB.state[summary.state as keyof typeof HUB.state];
  return (
    <Box
      component='button'
      type='button'
      onClick={onOpen}
      aria-label={`${say(summary.area.label, v)}: ${status}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        px: 2,
        py: 1.5,
        border: 0,
        borderBottom: last ? 0 : 1,
        borderColor: 'custom.neutral.200',
        bgcolor: 'transparent',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: -3 },
      }}
    >
      <Box sx={{ display: 'flex', color: done ? 'primary.main' : 'custom.neutral.400' }}>
        <Icon
          icon={(done ? 'circle-check' : summary.state === 'in_progress' ? 'clock' : 'circle') as never}
          fontSize={18}
        />
      </Box>
      <Typography sx={{ flex: 1 }} variant='body1'>
        {say(summary.area.label, v)}
      </Typography>
      <Typography sx={{ color: 'text.secondary' }} variant='body2'>
        {status}
      </Typography>
      <Icon icon={'chevron-right' as never} fontSize={12} />
    </Box>
  );
};

/**
 * The claim's home between rounds (spec 3.1): how close it is in words, the
 * quickest next step, and every area with its own small remainder. Never the
 * size of the form.
 */
const HubScreen = ({
  claim,
  today,
  onStep,
  onCheck,
  onLeave,
  onSeenArrivals,
}: {
  claim: ClaimState;
  today: Date;
  onStep: (step: Step) => void;
  onCheck: () => void;
  onLeave?: () => void;
  onSeenArrivals?: () => void;
}) => {
  const v = voiceOf(claim);
  const { facts } = claim;
  const [area, setArea] = useState<AreaSummary | null>(null);
  const at = progress(facts);
  const ready = isReady(facts);
  // Talking, typing and photos are read by AI, so a "no" takes them off the hub.
  const steps = nextSteps(facts, claim.rounds).filter(
    (s) => claim.aiConsent !== 'no' || s.kind === 'ask' || s.kind === 'batch',
  );
  const [first, ...rest] = steps;
  const areas = hubAreas(facts);
  const latest = claim.log[claim.log.length - 1];
  const by = sendBy(facts);
  const requestedOn = parseDate(text(facts, 'claimFormRequestedOn'));
  const qualifies = qualifyingDate(facts);
  const scenario = SCENARIO[claim.scenario];
  const arrivals = claim.arrivals.filter((id) => facts[id]?.source === 'call_notes');

  /** Every ask in an area with anything that applies. */
  const asksIn = (summary: AreaSummary) =>
    ASKS.filter(
      (a) =>
        a.area === summary.area.id &&
        a.entries.some((id) => statusOf(entry(id), facts) !== 'not_applicable'),
    );

  // Put off in Before we start, kept here so they are not forgotten (spec 9, 4).
  const tasks = [
    { ask: ASK['start-date'], label: HUB.tasksStartDate, icon: 'calendar' },
    { ask: ASK['signing-sr1'], label: HUB.tasksSr1, icon: 'file-lines' },
  ].filter((t) => openIn(t.ask, facts).length > 0 && t.ask.entries.every((id) => !facts[id]));

  const openAsk = (ask: Ask) => {
    setArea(null);
    onStep({ kind: 'ask', ask, score: 0, covers: openIn(ask, facts) });
  };

  return (
    <ClaimScreen
      bar={<ClaimBar onLeave={onLeave} />}
      title={HUB.headline[at]}
      description={say(HUB.intro[at], v)}
      footer={
        <MuiButton
          variant='contained'
          size='large'
          fullWidth
          disabled={!ready}
          onClick={onCheck}
        >
          {HUB.check}
        </MuiButton>
      }
    >
      {latest && (
        <Alert severity='success' role='status' icon={<Icon icon={'circle-check' as never} fontSize={16} />}>
          {latest.added.length
            ? HUB.added(
                latest.added.length,
                latest.area ? say(areaSummary(latest.area, facts).area.label, v) : 'the claim',
              )
            : HUB.addedNone}
        </Alert>
      )}

      {text(facts, 'specialRules') === 'Yes' && (
        <Chip sx={{ alignSelf: 'flex-start' }} size='small' variant='outlined' label={HUB.quicker} />
      )}

      {by && (
        <Alert severity='info' icon={<Icon icon={'calendar' as never} fontSize={16} />}>
          {HUB.sendBy(ukDate(by), requestedOn ? ukDate(requestedOn) : '')}
          {by.getTime() - today.getTime() < 10 * 86400000 && !ready && <> {HUB.sendByClose}</>}
        </Alert>
      )}

      {qualifies && qualifies > today && (
        <Alert severity='info' icon={<Icon icon={'clock' as never} fontSize={16} />}>
          {say(HUB.qualifying(ukDate(qualifies)), v)}
        </Alert>
      )}

      {arrivals.length > 0 && scenario.callNotes && (
        <Card sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant='overline'>{HUB.since}</Typography>
            <Typography variant='body2'>
              {HUB.sinceLine(arrivals.length, scenario.callNotes.date)}
            </Typography>
            {onSeenArrivals && (
              <MuiButton sx={{ alignSelf: 'flex-start' }} size='small' onClick={onSeenArrivals}>
                OK
              </MuiButton>
            )}
          </Stack>
        </Card>
      )}

      {tasks.length > 0 && (
        <Stack spacing={1}>
          <Typography component='h2' variant='h4'>
            {HUB.tasks}
          </Typography>
          {tasks.map((task) => (
            <ClaimActionRow
              key={task.ask.id}
              icon={task.icon}
              label={say(task.label, v)}
              onClick={() => openAsk(task.ask)}
            />
          ))}
        </Stack>
      )}

      {first && (
        <Stack spacing={1.5}>
          <Typography component='h2' variant='h4'>
            {ready ? HUB.stronger : HUB.quickest}
          </Typography>
          <StepCard step={first} claim={claim} onOpen={onStep} />
          {rest.slice(0, 2).map((step) => {
            const { title } = stepCopy(step, claim);
            return (
              <ClaimActionRow
                key={`${step.kind}-${title}`}
                icon={STEP_ICON[step.kind]}
                label={title}
                onClick={() => onStep(step)}
              />
            );
          })}
        </Stack>
      )}

      <Stack spacing={1.5}>
        <Typography component='h2' variant='h4'>
          {HUB.areasLabel}
        </Typography>
        <Card sx={{ p: 0, overflow: 'hidden' }}>
          {areas.map((summary, i) => (
            <AreaRow
              key={summary.area.id}
              summary={summary}
              claim={claim}
              last={i === areas.length - 1}
              onOpen={() => setArea(summary)}
            />
          ))}
        </Card>
      </Stack>

      {area && (
        <BottomSheet
          variant='hug'
          title={say(area.area.label, v)}
          onClose={() => setArea(null)}
        >
          <Stack spacing={1}>
            {/* Open asks first, then the finished ones, so an answer can be
                changed from here too. */}
            {[...area.asks, ...asksIn(area).filter((a) => !area.asks.includes(a))].map((ask) => {
              const open = area.asks.includes(ask);
              return (
                <ClaimActionRow
                  key={ask.id}
                  icon={open ? undefined : 'circle-check'}
                  tone={open ? undefined : 'go'}
                  label={open ? say(ask.title, v) : `${say(ask.title, v)}: ${HUB.change}`}
                  onClick={() => openAsk(ask)}
                />
              );
            })}
          </Stack>
        </BottomSheet>
      )}
    </ClaimScreen>
  );
};

export default HubScreen;
